<template>
  <div class="products-page">
    <!-- Page header -->
    <div class="page-header">
      <div class="container">
        <h1 class="page-title">Cushions & Accessories</h1>
        <p class="page-subtitle">Comfortable, safe, and practical accessory solutions for wheelchair users</p>
      </div>
    </div>

    <!-- Category navigation -->
    <div class="category-nav">
      <div class="container">
        <div class="category-tabs">
          <button 
            v-for="category in categories" 
            :key="category.id"
            :class="['category-tab', { active: selectedCategory === category.id }]"
            @click="selectCategory(category.id)"
          >
            <span class="category-icon">{{ category.icon }}</span>
            <span class="category-name">{{ category.name }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Product filters and listing -->
    <div class="products-section">
      <div class="container">
        <!-- Advanced filters -->
        <div class="advanced-filters">
          <div class="filter-header">
            <h3>Filters</h3>
            <button class="clear-filters" @click="clearFilters">Clear Filters</button>
          </div>
          
          <div class="filter-row">
            <!-- Basic filters -->
          <div class="filter-group">
              <label>Product Type</label>
              <select v-model="selectedCategory" class="filter-select">
                <option value="">All Types</option>
                <option value="cushions">Cushions</option>
                <option value="accessories">Wheelchair Accessories</option>
                <option value="safety">Safety Equipment</option>
                <option value="comfort">Comfort Items</option>
                <option value="storage">Storage Items</option>
            </select>
          </div>
            
          <div class="filter-group">
            <label>User Group</label>
            <select v-model="selectedUser" class="filter-select">
              <option value="">All</option>
              <option value="adult">Adult</option>
              <option value="child">Child</option>
              <option value="elderly">Elderly</option>
            </select>
          </div>
            
            <div class="filter-group">
              <label>Brand</label>
              <select v-model="selectedBrand" class="filter-select">
                <option value="">All Brands</option>
                <option value="permobil">Permobil</option>
                <option value="invacare">Invacare</option>
                <option value="sunrise">Sunrise</option>
                <option value="ottobock">Ottobock</option>
              </select>
            </div>
          </div>
          
          <div class="filter-row">
            <!-- Price range slider -->
            <div class="filter-group price-filter">
              <label>Price Range: ¥{{ priceRange[0].toLocaleString() }} - ¥{{ priceRange[1].toLocaleString() }}</label>
              <div class="price-slider">
                <input 
                  type="range" 
                  v-model="priceRange[0]" 
                  :min="0" 
                  :max="8000" 
                  step="100"
                  class="range-slider"
                />
                <input 
                  type="range" 
                  v-model="priceRange[1]" 
                  :min="0" 
                  :max="8000" 
                  step="100"
                  class="range-slider"
                />
              </div>
            </div>
            
            <!-- Rating filter -->
            <div class="filter-group">
              <label>Minimum Rating</label>
              <div class="rating-filter">
                <span 
                  v-for="rating in 5" 
                  :key="rating"
                  :class="['star', { active: selectedRating >= rating }]"
                  @click="selectedRating = rating"
                >
                  ★
                </span>
              </div>
            </div>
            
            <!-- Feature filters -->
            <div class="filter-group">
              <label>Features</label>
              <div class="feature-checkboxes">
                <label class="checkbox-item">
                  <input type="checkbox" v-model="selectedFeatures" value="comfort">
                  <span>Comfort Design</span>
                </label>
                <label class="checkbox-item">
                  <input type="checkbox" v-model="selectedFeatures" value="adjustable">
                  <span>Adjustable</span>
                </label>
                <label class="checkbox-item">
                  <input type="checkbox" v-model="selectedFeatures" value="lightweight">
                  <span>Lightweight</span>
                </label>
                <label class="checkbox-item">
                  <input type="checkbox" v-model="selectedFeatures" value="safety">
                  <span>Safety Protection</span>
                </label>
                <label class="checkbox-item">
                  <input type="checkbox" v-model="selectedFeatures" value="storage">
                  <span>Storage Function</span>
                </label>
              </div>
            </div>
          </div>
          
          <!-- Sort options -->
          <div class="filter-row">
            <div class="filter-group">
              <label>Sort By</label>
              <select v-model="sortBy" class="filter-select">
                <option value="default">Default</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="rating">Top Rated</option>
                <option value="newest">Newest</option>
                <option value="popular">Most Popular</option>
              </select>
            </div>
            
          <div class="filter-group">
              <label>Items per page</label>
              <select v-model="itemsPerPage" class="filter-select">
                <option value="6">6</option>
                <option value="12">12</option>
                <option value="24">24</option>
            </select>
            </div>
          </div>
        </div>

        <!-- Filter stats -->
        <div class="filter-stats">
          <span>Found {{ totalFilteredProducts }} products</span>
          <span v-if="activeFilters.length > 0" class="active-filters">
            Applied filters: {{ activeFilters.join(', ') }}
          </span>
        </div>

        <!-- Products grid -->
        <div class="products-grid">
          <div 
            v-for="product in paginatedProducts" 
            :key="product.id" 
            class="product-card"
            @click="viewProduct(product)"
          >
            <div class="product-image">
              <img :src="product.image" :alt="product.name" />
              <div class="product-badge" v-if="product.badge">{{ product.badge }}</div>
              <div class="product-rating">
                <span v-for="star in 5" :key="star" :class="['star', { filled: star <= product.rating }]">
                  ★
                </span>
                <span class="rating-text">({{ product.rating }})</span>
              </div>
            </div>
            <div class="product-info">
              <div class="product-header">
              <h3 class="product-name">{{ product.name }}</h3>
                <span class="product-brand">{{ product.brand }}</span>
              </div>
              <p class="product-desc">{{ product.description }}</p>
              <div class="product-features">
                <span v-for="feature in product.features" :key="feature" class="feature-tag">
                  {{ feature }}
                </span>
              </div>
              <div class="product-price">
                <span class="price">¥{{ product.price.toLocaleString() }}</span>
                <button class="view-details-btn">View Details</button>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div class="pagination">
          <button class="page-btn" :disabled="currentPage === 1" @click="changePage(-1)">
            Previous
          </button>
          <div class="page-numbers">
            <button 
              v-for="page in visiblePages" 
              :key="page"
              :class="['page-number', { active: page === currentPage }]"
              @click="currentPage = page"
            >
              {{ page }}
            </button>
          </div>
          <button class="page-btn" :disabled="currentPage === totalPages" @click="changePage(1)">
            Next
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Products',
  data() {
    return {
      selectedCategory: '',
      selectedUser: '',
      selectedBrand: '',
      selectedRating: 0,
      selectedFeatures: [],
      priceRange: [0, 8000],
      sortBy: 'default',
      itemsPerPage: 6,
      currentPage: 1,
      categories: [
        { id: 'cushions', name: 'Cushions', icon: '🪑' },
        { id: 'accessories', name: 'Wheelchair Accessories', icon: '🔧' },
        { id: 'safety', name: 'Safety Equipment', icon: '🛡️' },
        { id: 'comfort', name: 'Comfort Items', icon: '🛏️' },
        { id: 'storage', name: 'Storage Items', icon: '📦' }
      ],
      products: [
        {
          id: 1,
          name: '记忆棉防褥疮坐垫',
          description: '采用记忆棉材质，有效预防褥疮，提供舒适支撑',
          image: 'https://via.placeholder.com/300x200?text=记忆棉坐垫',
          price: 2800,
          category: 'cushions',
          userType: 'adult',
          brand: 'Permobil',
          rating: 4.8,
          features: ['防褥疮', '记忆棉', '透气设计'],
          productFeatures: ['comfort', 'adjustable'],
          badge: '热销',
          dateAdded: '2024-01-15',
          popularity: 95
        },
        {
          id: 2,
          name: '轮椅靠背垫',
          description: '柔软舒适的靠背垫，缓解背部疲劳，提供良好支撑',
          image: 'https://via.placeholder.com/300x200?text=靠背垫',
          price: 850,
          category: 'comfort',
          userType: 'adult',
          brand: 'Invacare',
          rating: 4.2,
          features: ['柔软舒适', '透气材质', '易清洗'],
          productFeatures: ['comfort', 'lightweight'],
          dateAdded: '2024-02-10',
          popularity: 78
        },
        {
          id: 3,
          name: '轮椅安全带',
          description: '安全可靠的安全带，确保用户安全，防止滑落',
          image: 'https://via.placeholder.com/300x200?text=安全带',
          price: 2200,
          category: 'safety',
          userType: 'adult',
          brand: 'Sunrise',
          rating: 4.6,
          features: ['安全可靠', '可调节', '舒适贴合'],
          productFeatures: ['adjustable', 'safety'],
          badge: '新品',
          dateAdded: '2024-03-05',
          popularity: 82
        },
        {
          id: 4,
          name: '儿童专用坐垫',
          description: '专为儿童设计，柔软舒适，安全无毒',
          image: 'https://via.placeholder.com/300x200?text=儿童坐垫',
          price: 1800,
          category: 'cushions',
          userType: 'child',
          brand: 'Ottobock',
          rating: 4.4,
          features: ['儿童专用', '安全无毒', '可调节'],
          productFeatures: ['adjustable', 'comfort'],
          dateAdded: '2024-01-20',
          popularity: 70
        },
        {
          id: 5,
          name: '轮椅收纳袋',
          description: '多功能收纳袋，方便携带个人物品，实用便捷',
          image: 'https://via.placeholder.com/300x200?text=收纳袋',
          price: 350,
          category: 'storage',
          userType: 'adult',
          brand: 'Permobil',
          rating: 4.7,
          features: ['多功能', '实用便捷', '防水材质'],
          productFeatures: ['lightweight', 'storage'],
          dateAdded: '2024-02-25',
          popularity: 88
        },
        {
          id: 6,
          name: '轮椅扶手垫',
          description: '柔软扶手垫，缓解手部疲劳，提升使用体验',
          image: 'https://via.placeholder.com/300x200?text=扶手垫',
          price: 1200,
          category: 'comfort',
          userType: 'adult',
          brand: 'Invacare',
          rating: 4.0,
          features: ['柔软舒适', '防滑设计', '易安装'],
          productFeatures: ['comfort'],
          dateAdded: '2024-03-15',
          popularity: 65
        },
        {
          id: 7,
          name: '高级凝胶坐垫',
          description: '采用凝胶材质，分散压力，提供极致舒适体验',
          image: 'https://via.placeholder.com/300x200?text=凝胶坐垫',
          price: 5500,
          category: 'cushions',
          userType: 'adult',
          brand: 'Permobil',
          rating: 4.9,
          features: ['凝胶材质', '分散压力', '极致舒适'],
          productFeatures: ['comfort', 'adjustable'],
          badge: '推荐',
          dateAdded: '2024-01-05',
          popularity: 92
        },
        {
          id: 8,
          name: '轮椅脚踏板',
          description: '可调节脚踏板，提供腿部支撑，提升舒适度',
          image: 'https://via.placeholder.com/300x200?text=脚踏板',
          price: 650,
          category: 'accessories',
          userType: 'adult',
          brand: 'Sunrise',
          rating: 4.3,
          features: ['可调节', '腿部支撑', '易安装'],
          productFeatures: ['adjustable', 'comfort'],
          dateAdded: '2024-02-20',
          popularity: 75
        },
        {
          id: 9,
          name: '轮椅防滑垫',
          description: '防滑垫设计，防止轮椅滑动，确保安全',
          image: 'https://via.placeholder.com/300x200?text=防滑垫',
          price: 450,
          category: 'safety',
          userType: 'adult',
          brand: 'Invacare',
          rating: 4.1,
          features: ['防滑设计', '安全可靠', '耐用材质'],
          productFeatures: ['safety'],
          dateAdded: '2024-03-10',
          popularity: 68
        },
        {
          id: 10,
          name: '轮椅杯架',
          description: '实用杯架，方便放置水杯，提升便利性',
          image: 'https://via.placeholder.com/300x200?text=杯架',
          price: 280,
          category: 'accessories',
          userType: 'adult',
          brand: 'Ottobock',
          rating: 4.5,
          features: ['实用设计', '易安装', '可调节'],
          productFeatures: ['adjustable', 'lightweight'],
          dateAdded: '2024-02-15',
          popularity: 72
        },
        {
          id: 11,
          name: '轮椅雨伞架',
          description: '雨天必备，方便携带雨伞，保护用户',
          image: 'https://via.placeholder.com/300x200?text=雨伞架',
          price: 380,
          category: 'accessories',
          userType: 'adult',
          brand: 'Sunrise',
          rating: 4.2,
          features: ['雨天必备', '易安装', '实用设计'],
          productFeatures: ['lightweight'],
          dateAdded: '2024-01-30',
          popularity: 60
        },
        {
          id: 12,
          name: '轮椅靠背调节器',
          description: '可调节靠背角度，满足不同需求，提升舒适度',
          image: 'https://via.placeholder.com/300x200?text=靠背调节器',
          price: 1200,
          category: 'accessories',
          userType: 'adult',
          brand: 'Permobil',
          rating: 4.6,
          features: ['可调节', '舒适设计', '易操作'],
          productFeatures: ['adjustable', 'comfort'],
          dateAdded: '2024-03-01',
          popularity: 80
        }
      ]
    }
  },
  computed: {
    filteredProducts() {
      let filtered = this.products.filter(product => {
        // 基础筛选
        if (this.selectedCategory && product.category !== this.selectedCategory) return false;
        if (this.selectedUser && product.userType !== this.selectedUser) return false;
        if (this.selectedBrand && product.brand !== this.selectedBrand) return false;
        
        // 价格范围筛选
        if (product.price < this.priceRange[0] || product.price > this.priceRange[1]) return false;
        
        // 评分筛选
        if (this.selectedRating > 0 && product.rating < this.selectedRating) return false;
        
        // 功能特性筛选
        if (this.selectedFeatures.length > 0) {
          const hasSelectedFeature = this.selectedFeatures.some(feature => 
            product.productFeatures.includes(feature)
          );
          if (!hasSelectedFeature) return false;
        }
        
        return true;
      });
      
      // 排序
      filtered.sort((a, b) => {
        switch (this.sortBy) {
          case 'price-low':
            return a.price - b.price;
          case 'price-high':
            return b.price - a.price;
          case 'rating':
            return b.rating - a.rating;
          case 'newest':
            return new Date(b.dateAdded) - new Date(a.dateAdded);
          case 'popular':
            return b.popularity - a.popularity;
          default:
            return 0;
        }
      });
      
      return filtered;
    },
    totalFilteredProducts() {
      return this.filteredProducts.length;
    },
    paginatedProducts() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredProducts.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.filteredProducts.length / this.itemsPerPage);
    },
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },
    activeFilters() {
      const filters = [];
      if (this.selectedCategory) filters.push('产品类型');
      if (this.selectedUser) filters.push('适用人群');
      if (this.selectedBrand) filters.push('品牌');
      if (this.selectedRating > 0) filters.push(`评分≥${this.selectedRating}`);
      if (this.selectedFeatures.length > 0) filters.push('功能特性');
      if (this.priceRange[0] > 0 || this.priceRange[1] < 8000) filters.push('价格范围');
      return filters;
    }
  },
  methods: {
    selectCategory(categoryId) {
      this.selectedCategory = categoryId;
      this.currentPage = 1;
    },
    changePage(delta) {
      const newPage = this.currentPage + delta;
      if (newPage >= 1 && newPage <= this.totalPages) {
        this.currentPage = newPage;
      }
    },
    clearFilters() {
      this.selectedCategory = '';
      this.selectedUser = '';
      this.selectedBrand = '';
      this.selectedRating = 0;
      this.selectedFeatures = [];
      this.priceRange = [0, 8000];
      this.sortBy = 'default';
      this.currentPage = 1;
    },
    viewProduct(product) {
      this.$router.push(`/product/${product.id}`);
    }
  },
  watch: {
    itemsPerPage() {
      this.currentPage = 1;
    }
  }
}
</script>

<style scoped>
.products-page {
  min-height: 100vh;
  background: #f8f9fa;
}

.page-header {
  background: linear-gradient(135deg, #2c5aa0 0%, #4a7bc8 100%);
  color: white;
  padding: 4rem 2rem;
  text-align: center;
}

.page-title {
  font-size: 3rem;
  margin-bottom: 1rem;
  font-weight: 300;
}

.page-subtitle {
  font-size: 1.2rem;
  opacity: 0.9;
}

.category-nav {
  background: white;
  border-bottom: 1px solid #e9ecef;
  padding: 1rem 0;
  position: sticky;
  top: 80px;
  z-index: 100;
}

.category-tabs {
  display: flex;
  gap: 1rem;
  overflow-x: auto;
  padding: 0.5rem 0;
}

.category-tab {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.8rem 1.5rem;
  border: none;
  background: transparent;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  font-size: 1rem;
  color: #666;
}

.category-tab:hover {
  background: #f8f9fa;
  color: #2c5aa0;
}

.category-tab.active {
  background: linear-gradient(135deg, #2c5aa0, #4a7bc8);
  color: white;
  box-shadow: 0 4px 15px rgba(44, 90, 160, 0.3);
}

.category-icon {
  font-size: 1.2rem;
}

.products-section {
  padding: 3rem 2rem;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
}

.filters {
  display: flex;
  gap: 2rem;
  margin-bottom: 3rem;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-group label {
  font-weight: 600;
  color: #333;
  font-size: 0.9rem;
}

.filter-select {
  padding: 0.8rem 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: white;
  font-size: 1rem;
  min-width: 150px;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
}

.product-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  transition: all 0.3s ease;
  cursor: pointer;
}

.product-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 30px rgba(0,0,0,0.12);
}

.product-image {
  position: relative;
  height: 200px;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-image img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.product-badge {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: #ff6b6b;
  color: white;
  padding: 0.3rem 0.8rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
}

.product-info {
  padding: 1.5rem;
}

.product-name {
  font-size: 1.3rem;
  margin-bottom: 0.5rem;
  color: #333;
  font-weight: 600;
}

.product-desc {
  color: #666;
  margin-bottom: 1rem;
  line-height: 1.5;
}

.product-features {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.feature-tag {
  background: #e3f2fd;
  color: #2c5aa0;
  padding: 0.3rem 0.8rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
}

.product-price {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.price {
  font-size: 1.4rem;
  font-weight: 700;
  color: #2c5aa0;
}

.view-details-btn {
  background: linear-gradient(135deg, #2c5aa0, #4a7bc8);
  color: white;
  border: none;
  padding: 0.6rem 1.2rem;
  border-radius: 20px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.3s ease;
}

.view-details-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(44, 90, 160, 0.3);
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
}

.page-btn {
  background: white;
  border: 1px solid #ddd;
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.page-btn:hover:not(:disabled) {
  background: #f8f9fa;
  border-color: #2c5aa0;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-weight: 600;
  color: #666;
}

.advanced-filters {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.filter-header h3 {
  color: #333;
  font-size: 1.2rem;
  font-weight: 600;
}

.clear-filters {
  background: #f8f9fa;
  color: #666;
  border: 1px solid #ddd;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.clear-filters:hover {
  background: #e9ecef;
  color: #333;
}

.filter-row {
  display: flex;
  gap: 2rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 200px;
}

.filter-group label {
  font-weight: 600;
  color: #333;
  font-size: 0.9rem;
}

.filter-select {
  padding: 0.8rem 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: white;
  font-size: 1rem;
  transition: border-color 0.3s ease;
}

.filter-select:focus {
  outline: none;
  border-color: #2c5aa0;
  box-shadow: 0 0 0 3px rgba(44, 90, 160, 0.1);
}

/* 价格滑块样式 */
.price-filter {
  flex: 2;
}

.price-slider {
  position: relative;
  height: 40px;
  display: flex;
  align-items: center;
}

.range-slider {
  position: absolute;
  width: 100%;
  height: 6px;
  background: #e9ecef;
  border-radius: 3px;
  outline: none;
  -webkit-appearance: none;
}

.range-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 20px;
  height: 20px;
  background: #2c5aa0;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(44, 90, 160, 0.3);
}

.range-slider::-moz-range-thumb {
  width: 20px;
  height: 20px;
  background: #2c5aa0;
  border-radius: 50%;
  cursor: pointer;
  border: none;
  box-shadow: 0 2px 6px rgba(44, 90, 160, 0.3);
}

/* 评分筛选样式 */
.rating-filter {
  display: flex;
  gap: 0.2rem;
}

.star {
  font-size: 1.5rem;
  color: #ddd;
  cursor: pointer;
  transition: color 0.3s ease;
}

.star.active,
.star.filled {
  color: #ffd700;
}

.star:hover {
  color: #ffd700;
}

/* 功能特性复选框 */
.feature-checkboxes {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.checkbox-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.9rem;
  color: #666;
}

.checkbox-item input[type="checkbox"] {
  width: 16px;
  height: 16px;
  accent-color: #2c5aa0;
}

/* 筛选统计 */
.filter-stats {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  font-size: 0.9rem;
}

.active-filters {
  color: #2c5aa0;
  font-weight: 500;
}

/* 产品卡片增强 */
.product-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
}

.product-brand {
  background: #e3f2fd;
  color: #2c5aa0;
  padding: 0.2rem 0.6rem;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 500;
}

.product-rating {
  position: absolute;
  bottom: 1rem;
  left: 1rem;
  background: rgba(255, 255, 255, 0.9);
  padding: 0.3rem 0.6rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

.rating-text {
  font-size: 0.8rem;
  color: #666;
}

/* 分页增强 */
.page-numbers {
  display: flex;
  gap: 0.5rem;
}

.page-number {
  background: white;
  border: 1px solid #ddd;
  padding: 0.8rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 40px;
  text-align: center;
}

.page-number:hover:not(.active) {
  background: #f8f9fa;
  border-color: #2c5aa0;
}

.page-number.active {
  background: #2c5aa0;
  color: white;
  border-color: #2c5aa0;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .page-title {
    font-size: 2rem;
  }
  
  .category-tabs {
    gap: 0.5rem;
  }
  
  .category-tab {
    padding: 0.6rem 1rem;
    font-size: 0.9rem;
  }
  
  .filters {
    flex-direction: column;
    gap: 1rem;
  }
  
  .products-grid {
    grid-template-columns: 1fr;
  }

  .filter-row {
    flex-direction: column;
    gap: 1rem;
  }
  
  .filter-group {
    min-width: auto;
  }
  
  .price-filter {
    flex: none;
  }
  
  .feature-checkboxes {
    gap: 0.5rem;
  }
  
  .filter-stats {
    flex-direction: column;
    gap: 0.5rem;
    text-align: center;
  }
  
  .page-numbers {
    display: none;
  }
}
</style> 